import { KNOWN_CATEGORIES } from './categories.ts';
import type { JobGrade, PageGrade } from './grading.ts';
import type { Submission } from './types.ts';

// Bucket key used for free-text "Other" categories that fall outside
// KNOWN_CATEGORIES. Mirrors `disciplineOf()` in grading.ts.
export const OTHER_BUCKET = '__other__';

export interface CategoryAxisEntry {
  readonly value: string;
  readonly label: string;
  readonly group: 'discipline' | 'special' | 'other';
}

// Axis order for the confusion matrix and the discipline breakdown.
// Disciplines first (so the diagonal clusters them), then specials, then the
// catch-all `__other__` bucket.
export const CATEGORY_AXIS: readonly CategoryAxisEntry[] = [
  ...KNOWN_CATEGORIES.filter((c) => c.group === 'discipline').map((c) => ({
    value: c.value,
    label: c.label,
    group: 'discipline' as const,
  })),
  ...KNOWN_CATEGORIES.filter((c) => c.group === 'special').map((c) => ({
    value: c.value,
    label: c.label,
    group: 'special' as const,
  })),
  { value: OTHER_BUCKET, label: 'Other', group: 'other' },
];

export interface ConfusionCell {
  readonly predicted: string;
  readonly truth: string;
  readonly count: number;
}

export interface ConfusionMatrix {
  readonly axis: readonly CategoryAxisEntry[];
  // Row-major lookup: cells[predicted][truth] = count.
  readonly cells: ReadonlyMap<string, ReadonlyMap<string, number>>;
  readonly otherPredictedRaw: readonly string[];
  readonly otherTruthRaw: readonly string[];
  readonly totalGraded: number;
}

// Build a confusion matrix over every graded page across all corpus jobs.
// Skipped, no_truth, and no_prediction outcomes are excluded so the matrix
// only reflects predictions that had a real answer to compare against.
export function buildConfusionMatrix(grades: readonly JobGrade[]): ConfusionMatrix {
  const cells = new Map<string, Map<string, number>>();
  const otherPredicted = new Set<string>();
  const otherTruth = new Set<string>();
  let totalGraded = 0;

  for (const entry of CATEGORY_AXIS) {
    cells.set(entry.value, new Map<string, number>());
  }

  for (const job of grades) {
    for (const page of job.pageGrades) {
      if (!isMatrixOutcome(page)) continue;
      const predictedKey = bucketKey(page.predicted_discipline);
      const truthKey = bucketKey(page.truth_discipline);
      const row = cells.get(predictedKey);
      if (!row) continue;
      row.set(truthKey, (row.get(truthKey) ?? 0) + 1);
      totalGraded += 1;

      if (predictedKey === OTHER_BUCKET && page.predicted_category !== null) {
        otherPredicted.add(page.predicted_category);
      }
      if (truthKey === OTHER_BUCKET && page.truth_category !== null) {
        otherTruth.add(page.truth_category);
      }
    }
  }

  return {
    axis: CATEGORY_AXIS,
    cells,
    otherPredictedRaw: [...otherPredicted].sort(),
    otherTruthRaw: [...otherTruth].sort(),
    totalGraded,
  };
}

function isMatrixOutcome(page: PageGrade): boolean {
  return (
    page.outcome === 'exact' ||
    page.outcome === 'sub_category_mismatch' ||
    page.outcome === 'discipline_mismatch'
  );
}

function bucketKey(discipline: string | null): string {
  if (discipline === null) return OTHER_BUCKET;
  return discipline;
}

export interface DisciplineRow {
  readonly value: string;
  readonly label: string;
  readonly group: 'discipline' | 'special' | 'other';
  readonly pageCount: number;
  readonly exactPct: number;
  readonly subMismatchPct: number;
  readonly disciplineMismatchPct: number;
}

// Per-truth-discipline breakdown: how does each category score? Pages where
// truth has no discipline (skipped, no_truth) are excluded. Percentages are
// rounded to one decimal so the table reads cleanly.
export function buildDisciplineBreakdown(grades: readonly JobGrade[]): readonly DisciplineRow[] {
  const counts = new Map<string, { total: number; exact: number; sub: number; disc: number }>();
  for (const entry of CATEGORY_AXIS) {
    counts.set(entry.value, { total: 0, exact: 0, sub: 0, disc: 0 });
  }

  for (const job of grades) {
    for (const page of job.pageGrades) {
      if (!isMatrixOutcome(page)) continue;
      const key = bucketKey(page.truth_discipline);
      const bucket = counts.get(key);
      if (!bucket) continue;
      bucket.total += 1;
      if (page.outcome === 'exact') bucket.exact += 1;
      else if (page.outcome === 'sub_category_mismatch') bucket.sub += 1;
      else if (page.outcome === 'discipline_mismatch') bucket.disc += 1;
    }
  }

  return CATEGORY_AXIS.map((entry) => {
    const bucket = counts.get(entry.value) ?? { total: 0, exact: 0, sub: 0, disc: 0 };
    return {
      value: entry.value,
      label: entry.label,
      group: entry.group,
      pageCount: bucket.total,
      exactPct: pct(bucket.exact, bucket.total),
      subMismatchPct: pct(bucket.sub, bucket.total),
      disciplineMismatchPct: pct(bucket.disc, bucket.total),
    };
  });
}

function pct(num: number, denom: number): number {
  if (denom === 0) return 0;
  return Math.round((num / denom) * 1000) / 10;
}

export interface TrendBucket {
  readonly monthKey: string;
  readonly label: string;
  readonly jobCount: number;
  readonly meanScorePct: number;
}

export interface BuildTrendOptions {
  readonly now?: Date;
  readonly months?: number;
}

// Twelve trailing months by default. Uses each submission's `promoted_at`
// from its ground_truth snapshot (the rule from the plan: promoted_at lives
// in JSON, not as a column). Submissions without a snapshot are dropped.
export function buildTrendByMonth(
  grades: readonly JobGrade[],
  submissions: readonly Submission[],
  options: BuildTrendOptions = {},
): readonly TrendBucket[] {
  const months = options.months ?? 12;
  const now = options.now ?? new Date();
  const submissionById = new Map(submissions.map((s) => [s.id, s] as const));

  const buckets = new Map<string, { scoreSum: number; count: number }>();
  const ordered: string[] = [];
  const labelByKey = new Map<string, string>();

  for (let i = months - 1; i >= 0; i -= 1) {
    const ref = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = monthKey(ref);
    ordered.push(key);
    buckets.set(key, { scoreSum: 0, count: 0 });
    labelByKey.set(key, monthLabel(ref));
  }

  for (const job of grades) {
    const sub = submissionById.get(job.submissionId);
    const promotedAt = sub?.ground_truth?.promoted_at;
    if (!promotedAt) continue;
    const date = new Date(promotedAt);
    if (Number.isNaN(date.getTime())) continue;
    const key = monthKey(date);
    const bucket = buckets.get(key);
    if (!bucket) continue;
    bucket.scoreSum += job.scorePct;
    bucket.count += 1;
  }

  return ordered.map((key) => {
    const bucket = buckets.get(key) ?? { scoreSum: 0, count: 0 };
    return {
      monthKey: key,
      label: labelByKey.get(key) ?? key,
      jobCount: bucket.count,
      meanScorePct: bucket.count === 0 ? 0 : Math.round((bucket.scoreSum / bucket.count) * 10) / 10,
    };
  });
}

function monthKey(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthLabel(date: Date): string {
  return `${MONTH_LABELS[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(-2)}`;
}

export interface FleetSummary {
  readonly corpusJobCount: number;
  readonly totalGradedPages: number;
  readonly meanScorePct: number;
  readonly lastPromotedAt: string | null;
}

// Top-of-dashboard summary. Mean score is weighted by graded-page count so a
// single huge job doesn't get drowned out by tiny ones (or vice versa).
export function buildFleetSummary(
  grades: readonly JobGrade[],
  submissions: readonly Submission[],
): FleetSummary {
  let totalGradedPages = 0;
  let weightedScoreSum = 0;
  for (const job of grades) {
    totalGradedPages += job.totalGraded;
    weightedScoreSum += job.scorePct * job.totalGraded;
  }

  let lastPromotedAt: string | null = null;
  for (const sub of submissions) {
    const ts = sub.ground_truth?.promoted_at;
    if (!ts) continue;
    if (lastPromotedAt === null || ts > lastPromotedAt) lastPromotedAt = ts;
  }

  return {
    corpusJobCount: grades.length,
    totalGradedPages,
    meanScorePct:
      totalGradedPages === 0 ? 0 : Math.round((weightedScoreSum / totalGradedPages) * 10) / 10,
    lastPromotedAt,
  };
}
